import type {
  AppConfiguration,
  AppDefinition,
  SessionRecord,
} from '@web/api/types'
import { useIdentity } from '@web/state/identity'
import { useState } from 'react'

export function appConfiguration(
  app: AppDefinition,
  record?: SessionRecord | null,
): AppConfiguration {
  return (
    record?.config.app_configuration ??
    app.descriptor?.configuration ?? {
      ui: {
        mode: app.launchMode === 'chat' ? 'chat' : 'hybrid',
        position: 'right',
        width_percent: 60,
        resizable: true,
        minimized: false,
      },
      frontend: {},
      backend: { mode: 'none' },
      sessions: {
        nature: 'independent',
        history_minimized: false,
        idle_timeout_minutes: 30,
      },
      status_bar: {
        items: ['model', 'effort', 'permissions', 'context', 'connection'],
      },
    }
  )
}

/** Store display preferences independently for each account, organization, and app. */
export function useAppPreference<T>(
  appId: string,
  name: string,
  fallback: T,
): [T, (next: T) => void] {
  const identity = useIdentity()
  const key = `bridge.layout.${identity.state?.current_user?.id ?? 'guest'}.${identity.state?.current_org?.id ?? 'none'}.${appId}.${name}`
  const read = () => {
    try {
      const stored = localStorage.getItem(key)
      if (stored === null) return fallback
      const parsed: unknown = JSON.parse(stored)
      if (
        typeof parsed !== typeof fallback ||
        (typeof parsed === 'number' && !Number.isFinite(parsed))
      )
        return fallback
      return parsed as T
    } catch {
      return fallback
    }
  }
  const [saved, setSaved] = useState<{ key: string; value: T }>(() => ({
    key,
    value: read(),
  }))
  const value = saved.key === key ? saved.value : read()
  return [
    value,
    (next) => {
      setSaved({ key, value: next })
      try {
        localStorage.setItem(key, JSON.stringify(next))
      } catch {
        /* Private browsing may disable persistence. */
      }
    },
  ]
}
